import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Calendar, ExternalLink, Image } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RendersPage = () => {
  const { studio } = useOutletContext();
  const { getAuthHeader } = useAuth();
  const [renders, setRenders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRenders();
  }, []);

  const fetchRenders = async () => {
    try {
      const response = await axios.get(`${API}/renders/studio/me`, { headers: getAuthHeader() });
      setRenders(response.data);
    } catch (error) {
      console.error('Failed to fetch renders:', error);
    } finally {
      setLoading(false);
    }
  };

  const statusColors = {
    pending: 'bg-yellow-500/20 text-yellow-400',
    processing: 'bg-blue-500/20 text-blue-400',
    completed: 'bg-green-500/20 text-green-400',
    fallback: 'bg-orange-500/20 text-orange-400',
    failed: 'bg-red-500/20 text-red-400'
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="renders-page"
    >
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Renders</h1>
        <p className="text-white/60">{renders.length} total renders generated</p>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="aspect-video bg-[#0F0F0F] rounded-sm animate-pulse"></div>
          ))}
        </div>
      ) : renders.length === 0 ? (
        <div className="text-center py-16 bg-[#0F0F0F] border border-white/10 rounded-sm">
          <Image className="w-12 h-12 mx-auto mb-4 text-white/20" />
          <p className="text-white/40 mb-2">No renders yet</p>
          <p className="text-white/30 text-sm">Renders will appear here when customers use your visualizer</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {renders.map((render) => (
            <div 
              key={render.id}
              className="bg-[#0F0F0F] border border-white/10 rounded-sm overflow-hidden"
              data-testid={`render-${render.id}`}
            >
              {/* Image Preview */}
              <div className="aspect-video bg-[#171717] relative">
                {render.result_image_url ? (
                  <img 
                    src={render.result_image_url} 
                    alt="Render result"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="spinner w-8 h-8" style={{ borderTopColor: '#FFFFFF' }}></span>
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-xs px-2 py-1 rounded capitalize ${statusColors[render.status]}`}>
                    {render.status}
                  </span>
                  <span className="text-xs text-white/40 flex items-center gap-1">
                    <Calendar size={12} />
                    {formatDate(render.created_at)}
                  </span>
                </div>

                {render.status === 'completed' && studio && (
                  <a
                    href={`/s/${studio.slug}/result/${render.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#FFFFFF] text-sm hover:underline flex items-center gap-1"
                  >
                    View Result <ExternalLink size={12} />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default RendersPage;
